import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { AuthTokenService } from './auth-token.service';

/**
 * Authentication Token Manager
 * Coordinates token fetching, storage, and retrieval
 * Automatically handles token refresh when expired
 */
@Injectable({
  providedIn: 'root'
})
export class AuthTokenManagerService {
  private pendingTokenRequests: Map<string, Promise<string>> = new Map();

  constructor(
    private http: HttpClient,
    private authTokenService: AuthTokenService
  ) {}

  /**
   * Get a valid auth token for the session
   * Reuses cached token from cookie if still valid
   * Fetches new token if expired or missing
   *
   * @param sessionId - Session ID to get token for
   * @param baseURL - Base URL for API requests
   * @returns Promise resolving to the auth token
   */
  async getToken(sessionId: string, baseURL: string): Promise<string> {
    // Check if token exists in cookie and is still valid
    const cachedToken = this.authTokenService.getToken(sessionId);
    if (cachedToken) {
      console.log(`[TokenManager] Using cached token for session ${sessionId}`);
      return cachedToken;
    }

    // Check if there's already a pending request for this session
    const pendingRequest = this.pendingTokenRequests.get(sessionId);
    if (pendingRequest) {
      console.log(`[TokenManager] Waiting for pending token request for session ${sessionId}`);
      return pendingRequest;
    }

    // Fetch new token
    console.log(`[TokenManager] Fetching new token for session ${sessionId}`);
    const tokenPromise = this.fetchNewToken(sessionId, baseURL);

    // Store pending request to prevent duplicate calls
    this.pendingTokenRequests.set(sessionId, tokenPromise);

    try {
      const token = await tokenPromise;
      return token;
    } finally {
      // Cleanup pending request
      this.pendingTokenRequests.delete(sessionId);
    }
  }

  /**
   * Fetch a new auth token from the server
   * @param sessionId - Session ID
   * @param baseURL - Base URL for API requests
   * @returns Promise resolving to the new token
   */
  private async fetchNewToken(sessionId: string, baseURL: string): Promise<string> {
    try {
      const url = `${baseURL}z/x/${sessionId}`;

      const response = await lastValueFrom(
        this.http.post<{ aut: string }>(url, {}, {
          headers: new HttpHeaders({
            'Content-Type': 'application/json'
          })
        })
      );

      if (!response.aut) {
        throw new Error('No auth token in response');
      }

      const token = response.aut;

      // Store token in secure cookie
      this.authTokenService.setToken(sessionId, token);

      console.log(`[TokenManager] New token fetched and stored for session ${sessionId}`);
      return token;
    } catch (error) {
      console.error(`[TokenManager] Failed to fetch token for session ${sessionId}:`, error);
      throw error;
    }
  }

  /**
   * Execute a protected operation with automatic token management
   * Handles token fetch, storage, and retry on auth failure
   *
   * @param sessionId - Session ID
   * @param baseURL - Base URL for API requests
   * @param operation - Function that performs the protected operation
   * @returns Promise resolving to the operation result
   */
  async executeWithAuth<T>(
    sessionId: string,
    baseURL: string,
    operation: (token: string) => Promise<T>
  ): Promise<T> {
    try {
      // Get token (from cache or fetch new)
      const token = await this.getToken(sessionId, baseURL);

      // Execute operation with token
      return await operation(token);
    } catch (error: any) {
      // If auth failed (401), token might have expired
      // Remove from cache and retry once with fresh token
      if (error?.status === 401) {
        console.warn(`[TokenManager] Auth failed for session ${sessionId}, retrying with fresh token`);

        // Clear expired token
        this.authTokenService.removeToken(sessionId);

        // Fetch fresh token and retry
        const freshToken = await this.fetchNewToken(sessionId, baseURL);
        return await operation(freshToken);
      }

      // Other errors - just rethrow
      throw error;
    }
  }

  /**
   * Clear token for a specific session
   * @param sessionId - Session ID to clear
   */
  clearToken(sessionId: string): void {
    this.authTokenService.removeToken(sessionId);
    console.log(`[TokenManager] Token cleared for session ${sessionId}`);
  }

  /**
   * Clear all stored tokens
   */
  clearAllTokens(): void {
    this.authTokenService.clearAllTokens();
    this.pendingTokenRequests.clear();
    console.log('[TokenManager] All tokens cleared');
  }

  /**
   * Check if a valid token exists for the session
   * @param sessionId - Session ID to check
   * @returns true if valid token exists
   */
  hasValidToken(sessionId: string): boolean {
    return this.authTokenService.hasValidToken(sessionId);
  }
}
